/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

// Component imports
import { Badge, Button, Col, Form, Input, Popconfirm, Row, Space, Spin, Switch, Table, Tooltip, Typography } from 'antd';
import { CaretRightOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import SideModal from '../Layout/SideModal';
import ScheduledFlowForm from '../Forms/ScheduledFlowForm';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import {
  useGetScheduledFlowsQuery,
  useGetScheduledFlowExecutionsQuery,
  useActivateScheduledFlowMutation,
  useDeactivateScheduledFlowMutation,
  useDeleteScheduledFlowMutation,
  useTriggerScheduledFlowExecutionMutation
} from '../../store/scheduledFlows/scheduledFlowsApi';
import { DATETIME_BACKEND_FORMAT, DAYS_OF_WEEK, DEFAULT_DATETIME_TIMEZONE_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchActivitiesQuery } from '../../store/activities/activitiesApi';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { NODE_TYPES } from '../../constants/flowGraph';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';
import useDebouncedCallback from '../../utils/hooks/useDebounce';

const EXECUTION_PAGE_SIZE = 10;

const BADGE_STATUS_MAP = { success: 'success', failed: 'error', running: 'processing' };

const FILTER_PARAM_KEYS = ['searchTerm', 'active', 'lastRunStatus', 'sortBy', 'order', 'page'];

const ExecutionHistory = ({ record, orgId, tenantId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [execPage, setExecPage] = useState(0);
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);

  const { data: execData, isFetching } = useGetScheduledFlowExecutionsQuery(
    { id: record.id, orgId, tenantId, page: execPage, pageSize: EXECUTION_PAGE_SIZE },
    { skip: !record.id, refetchOnMountOrArgChange: true }
  );

  const executions = execData?.items || [];
  const hasMore = execData ? execPage + 1 < Math.ceil(execData.total / EXECUTION_PAGE_SIZE) : false;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollContainerRef.current;

    if (!sentinel || !container || !hasMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetching) {
          setExecPage((prev) => prev + 1);
        }
      },
      { root: container, rootMargin: '0px', threshold: 0.2 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [isFetching, hasMore]);

  const buildObservatoryUrl = (execution) => {
    if (!execution.tag || !execution.startedAt) {
      return null;
    }
    const startOfDay = moment(execution.startedAt).startOf('day').format(DATETIME_BACKEND_FORMAT);
    const endOfDay = moment(execution.startedAt).endOf('day').format(DATETIME_BACKEND_FORMAT);
    const tenantsFilter = JSON.stringify([{ tenant: tenantId }]);
    return `/observatory/${orgId}/chat-sessions?`
      + `startDate=${encodeURIComponent(startOfDay)}`
      + `&endDate=${encodeURIComponent(endOfDay)}`
      + `&tenantsFilter=${encodeURIComponent(tenantsFilter)}`
      + `&tags=${encodeURIComponent(execution.tag)}`
      + `&searchBy=tag`;
  };

  const executionColumns = [
    {
      title: t('Timestamp'),
      key: 'startedAt',
      width: '25%',
      render: (exec) => exec.startedAt ? moment(exec.startedAt).format(DEFAULT_DATETIME_TIMEZONE_FORMAT) : '-'
    },
    {
      title: t('Status'),
      key: 'status',
      width: '12%',
      render: (exec) => {
        const statusMap = {
          success: { status: 'success', text: t('Success') },
          failed: { status: 'error', text: t('Failed') },
          running: { status: 'processing', text: t('Running') }
        };
        const s = statusMap[exec.status] || { status: 'default', text: exec.status };
        return <Badge status={s.status} text={s.text} />;
      }
    },
    {
      title: t('Duration'),
      key: 'durationMs',
      width: '10%',
      render: (exec) => exec.durationMs != null ? `${(exec.durationMs / 1000).toFixed(1)}s` : '-'
    },
    {
      title: t('Retries'),
      key: 'retryCount',
      width: '8%',
      render: (exec) => exec.retryCount || 0
    },
    {
      title: t('Error'),
      key: 'error',
      width: '38%',
      ellipsis: { showTitle: false },
      render: (exec) => exec.error
        ? (
          <Typography.Text
            className="text-danger mb-0"
            ellipsis={{ tooltip: { title: exec.error, overlayClassName: 'pointer-events-none' } }}
          >
            {exec.error}
          </Typography.Text>
        )
        : '-'
    },
    {
      title: '',
      key: 'actions',
      width: '7%',
      align: 'center',
      render: (exec) => {
        const url = buildObservatoryUrl(exec);
        if (!url) {
          return null;
        }
        return (
          <Tooltip title={t('View Sessions')}>
            <Button
              type="text"
              shape="circle"
              icon={<EyeOutlined />}
              onClick={() => navigate(url)}
            />
          </Tooltip>
        );
      }
    }
  ];

  return (
    <div ref={scrollContainerRef} className="execution-history-table overflow-y-auto scrollbar-custom" style={{ maxHeight: 300 }}>
      <Table
        size="small"
        rowKey="id"
        columns={executionColumns}
        dataSource={executions}
        loading={isFetching && execPage === 0}
        pagination={false}
        locale={{ emptyText: t('No executions yet') }}
      />
      {isFetching && execPage > 0 && (
        <div className="text-center py-2">
          <Spin />
        </div>
      )}
      <div ref={sentinelRef} style={{ height: 1 }} />
    </div>
  );
};

ExecutionHistory.propTypes = {
  record: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  orgId: PropTypes.string.isRequired,
  tenantId: PropTypes.string.isRequired
};

const ScheduledFlowsTable = () => {
  const [form] = Form.useForm();
  const { tenantId, orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const page = Number(searchParams.get('page') || 0);
  const pageSize = Number(searchParams.get('pageSize') || DEFAULT_PAGINATION_SIZE);

  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const timezone = bot?.timezone || 'UTC';
  const { data: activities } = useFetchActivitiesQuery({ orgId, tenantId }, { skip: !orgId || !tenantId });
  const { primaryBotLanguage } = useBotLanguage();

  const urlParams = useMemo(() => {
    const params = { page, pageSize };
    const optionalKeys = ['sortBy', 'order', 'searchTerm', 'active', 'lastRunStatus'];
    optionalKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    });
    return params;
  }, [searchParams, page, pageSize]);

  const { data: scheduledFlows, isLoading: isLoadingScheduledFlows } = useGetScheduledFlowsQuery(
    { orgId, tenantId, urlParams },
    { skip: !orgId || !tenantId }
  );
  const [activateScheduledFlow] = useActivateScheduledFlowMutation();
  const [deactivateScheduledFlow] = useDeactivateScheduledFlowMutation();
  const [deleteScheduledFlow] = useDeleteScheduledFlowMutation();
  const [triggerExecution] = useTriggerScheduledFlowExecutionMutation();

  const flowNameByNodeId = useMemo(() => {
    if (!activities?.items) {
      return {};
    }
    const map = {};
    activities.items.forEach(activity => {
      const introNode = (activity?.content?.nodes || []).find(node => node.type === NODE_TYPES.INTRO);
      if (introNode) {
        map[introNode.id] = activity.name[primaryBotLanguage] || activity.id;
      }
    });
    return map;
  }, [activities, primaryBotLanguage]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [triggeringId, setTriggeringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const sortingInfo = useMemo(() => {
    const orderParam = searchParams.get('order');
    let order = null;
    if (orderParam === 'desc') {
      order = 'descend';
    } else if (orderParam === 'asc') {
      order = 'ascend';
    }
    const columnKey = searchParams.get('sortBy') || null;

    return { order, columnKey };
  }, [searchParams]);

  const updateUrlSearchParams = (current, newPageSize, filters, sorting) => {
    const urlSearchParams = new URLSearchParams(location.search);

    const searchTerm = form.getFieldValue('searchTerm');
    if (searchTerm) {
      urlSearchParams.set('searchTerm', searchTerm);
    } else {
      urlSearchParams.delete('searchTerm');
    }

    urlSearchParams.set('page', current !== undefined ? current - 1 : 0);
    urlSearchParams.set('pageSize', newPageSize || pageSize);

    if (sorting) {
      const [sortBy, order] = Object.entries(sorting).flat();
      if (sortBy && order) {
        urlSearchParams.set('sortBy', sortBy);
        urlSearchParams.set('order', order);
      } else {
        urlSearchParams.delete('sortBy');
        urlSearchParams.delete('order');
      }
    }

    if (filters) {
      if (filters.active?.length) {
        urlSearchParams.set('active', filters.active[0]);
      } else {
        urlSearchParams.delete('active');
      }
      if (filters.lastRunAt?.length) {
        urlSearchParams.set('lastRunStatus', filters.lastRunAt[0]);
      } else {
        urlSearchParams.delete('lastRunStatus');
      }
    }

    navigate({ pathname: location.pathname, search: urlSearchParams.toString() }, { replace: true });
  };

  const debouncedOnChange = useDebouncedCallback(() => {
    updateUrlSearchParams(undefined, pageSize);
  });

  useEffect(() => {
    form.setFieldsValue({
      searchTerm: searchParams.get('searchTerm') || ''
    });
  }, [form, searchParams]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onModalClose = () => {
    setIsModalOpen(false);
    setSelectedSchedule(null);
  };

  const onDelete = async (id) => {
    setDeletingId(id);
    await deleteScheduledFlow({ id, orgId, tenantId });
    setDeletingId(null);
  };

  const onToggleActive = async (record) => {
    if (record.active) {
      await deactivateScheduledFlow({ id: record.id, orgId, tenantId });
    } else {
      await activateScheduledFlow({ id: record.id, orgId, tenantId });
    }
  };

  const onTriggerNow = async (id) => {
    setTriggeringId(id);
    await triggerExecution({ id, orgId, tenantId });
    setTriggeringId(null);
  };

  const handleTableChange = (pagination, filters, sorter, extra) => {
    const sorting = sorter.column && sorter.order
      ? { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' }
      : {};

    const isFilterOrSortChange = extra?.action === 'filter' || extra?.action === 'sort';
    const newPage = isFilterOrSortChange ? undefined : pagination.current;

    updateUrlSearchParams(newPage, pagination.pageSize, filters, sorting);
  };

  const formatSchedule = (record) => {
    const dayLabels = [...(record?.daysOfWeek || [])]
      .sort((a, b) => a - b)
      .map(day => {
        const found = DAYS_OF_WEEK.find(d => d.value === day);
        return found ? found.label : '';
      })
      .filter(Boolean);

    const time = record.timeOfDay || '';
    if (dayLabels.length === 0) {
      return time;
    }
    return t('{{days}} at {{time}}', { days: dayLabels.join(', '), time });
  };

  const formatLastExecution = (record) => {
    if (!record.lastRunAt) {
      return <span className="text-muted">{t('Never')}</span>;
    }

    const formatted = moment(record.lastRunAt).tz(timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT);
    const status = (record.lastRunStatus || '').toLowerCase();

    return (
      <Space size={4}>
        <Badge status={BADGE_STATUS_MAP[status] || 'default'} />
        <span>{formatted}</span>
      </Space>
    );
  };

  const formatNextExecution = (record) => {
    if (!record.active || !record.nextRunAt) {
      return <span className="text-muted">-</span>;
    }
    return moment(record.nextRunAt).tz(timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT);
  };

  const formatCreatedAt = (record) => {
    if (!record.createdAt) {
      return <span className="text-muted">-</span>;
    }
    return moment(record.createdAt).tz(timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT);
  };

  const columns = [
    {
      title: t('Flow'),
      key: 'flowName',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'flowName' && sortingInfo.order,
      render: record => flowNameByNodeId[record.flowNodeId] || record.flowName || '-'
    },
    {
      title: t('Schedule'),
      key: 'schedule',
      render: record => formatSchedule(record)
    },
    {
      title: t('Created'),
      key: 'createdAt',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'createdAt' && sortingInfo.order,
      render: record => formatCreatedAt(record)
    },
    {
      title: t('Last Run'),
      key: 'lastRunAt',
      filters: [
        { text: t('Success'), value: 'success' },
        { text: t('Failed'), value: 'failed' },
        { text: t('Running'), value: 'running' }
      ],
      filterMultiple: false,
      filterIcon: useTableFilterIcon,
      filteredValue: searchParams.get('lastRunStatus') ? [searchParams.get('lastRunStatus')] : null,
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'lastRunAt' && sortingInfo.order,
      render: record => formatLastExecution(record)
    },
    {
      title: t('Next Run'),
      key: 'nextRunAt',
      render: record => formatNextExecution(record)
    },
    {
      title: t('Active'),
      key: 'active',
      width: 100,
      align: 'center',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'active' && sortingInfo.order,
      filters: [
        { text: t('Active'), value: 'true' },
        { text: t('Inactive'), value: 'false' }
      ],
      filterMultiple: false,
      filterIcon: useTableFilterIcon,
      filteredValue: searchParams.get('active') ? [searchParams.get('active')] : null,
      render: record => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            size="small"
            checked={record.active}
            onChange={() => onToggleActive(record)}
          />
        </div>
      )
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      width: 150,
      align: 'center',
      render: record => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Tooltip overlayClassName="pointer-events-none" title={t('Edit')}>
            <Button
              type="text"
              shape="circle"
              icon={<EditIcon />}
              onClick={() => {
                setSelectedSchedule(record);
                onModalOpen();
              }}
            />
          </Tooltip>

          <Tooltip overlayClassName="pointer-events-none" title={t('Run Now')}>
            <Button
              type="text"
              shape="circle"
              icon={<CaretRightOutlined />}
              loading={triggeringId === record.id}
              onClick={() => onTriggerNow(record.id)}
            />
          </Tooltip>

          <Popconfirm
            title={t('Are you sure you want to delete this schedule?')}
            okText={t('Yes')}
            cancelText={t('No')}
            placement="topRight"
            onConfirm={() => onDelete(record.id)}
          >
            <Tooltip overlayClassName="pointer-events-none" title={t('Delete')}>
              <Button
                type="text"
                shape="circle"
                icon={<DeleteIcon />}
                loading={deletingId === record.id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const items = scheduledFlows?.items || [];

  const hasActiveFilters = searchParams.get('searchTerm') || searchParams.get('active') || searchParams.get('lastRunStatus');

  const clearFilters = () => {
    const cleared = new URLSearchParams(location.search);
    FILTER_PARAM_KEYS.forEach(k => cleared.delete(k));
    form.setFieldsValue({ searchTerm: '' });
    navigate({ pathname: location.pathname, search: cleared.toString() }, { replace: true });
  };

  const emptyText = hasActiveFilters ? (
    <div>
      <p>{t('No schedules match your filters')}</p>
      <Button type="link" className="p-0" onClick={clearFilters}>
        {t('Clear Filters')}
      </Button>
    </div>
  ) : undefined;

  return (
    <Fragment>
      <Row gutter={16} className="mb-3" align="middle">
        <Col xs={4}>
          <Form
            form={form}
            initialValues={{
              searchTerm: searchParams.get('searchTerm') || ''
            }}
          >
            <Form.Item name="searchTerm" className="mb-0">
              <Input
                size="large"
                prefix={<SearchOutlined className="text-secondary" />}
                placeholder={t('Search by flow name')}
                allowClear
                onChange={debouncedOnChange}
              />
            </Form.Item>
          </Form>
        </Col>
        <Col flex="auto">
          <Button
            className="float-right"
            size="large"
            type="primary"
            ghost
            icon={<PlusIcon />}
            onClick={() => {
              setSelectedSchedule(null);
              onModalOpen();
            }}
          >
            {t('Add Schedule')}
          </Button>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            size="small"
            rowKey="id"
            onRow={(record) => ({
              onClick: (e) => {
                if (e.target.closest('.ant-table-row-expand-icon')) {
                  return;
                }
                setSelectedSchedule(record);
                onModalOpen();
              }
            })}
            rowClassName="cursor-pointer"
            expandable={{
              expandedRowRender: (record) => (
                <ExecutionHistory record={record} orgId={orgId} tenantId={tenantId} />
              )
            }}
            pagination={{
              size: 'default',
              current: page + 1,
              pageSize,
              total: scheduledFlows?.total || 0,
              showSizeChanger: true,
              showTotal: (total) => t('{{total}} schedules', { total })
            }}
            dataSource={items}
            columns={columns}
            loading={isLoadingScheduledFlows}
            onChange={handleTableChange}
            locale={emptyText ? { emptyText } : undefined}
          />
        </Col>
      </Row>

      <SideModal
        title={!selectedSchedule ? t('Create Schedule') : t('Edit Schedule')}
        open={isModalOpen}
        onClose={onModalClose}
        destroyOnClose
        bodyClassName="p-0"
      >
        <ScheduledFlowForm
          isUpdateMode={!!selectedSchedule}
          initialSchedule={selectedSchedule}
          onSubmitSuccess={onModalClose}
          onCancel={onModalClose}
          bodyClassName="px-6 pt-6 flex-grow-1"
          footerClassName="ant-drawer-footer"
        />
      </SideModal>
    </Fragment>
  );
};

export default ScheduledFlowsTable;
